import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import registerImage from '../../images/loginimage.jpg'
import styles from '../../styles/register.module.css'

function Register() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const validate = () => {
    if (!name.trim()) {
      toast.error('Please enter your name', {
        position: 'bottom-right',
        theme: 'colored'
      })
      return false
    }
    if (!email.trim()) {
      toast.error('Please enter your email address', {
        position: 'bottom-right',
        theme: 'colored'
      })
      return false
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters', {
        position: 'bottom-right',
        theme: 'colored'
      })
      return false
    }
    return true
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!validate()) return

    try {
      setLoading(true)

      const response = await axios.post(
        'https://chatting-club-backend.onrender.com/api/users',
        { name: name.trim(), email: email.trim(), password },
        { withCredentials: true }
      )

      toast.success('🎉 Account created successfully! Welcome to Chat!', {
        position: 'bottom-right',
        theme: 'colored'
      })
      setTimeout(() => navigate('/login'), 1500)
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.'
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = () => {
     window.location.href = 'https://chatting-club-backend.onrender.com/auth/callback'
  }

  return (
    <>
      <div className={styles.registerPage}>
        <main className={styles.page}>
          <section className={styles.card}>
            <aside className={styles.banner}>
              <img src={registerImage} alt="Chat community" className={styles.bannerImage} />
              <div className={styles.bannerOverlay} />

              <div className={styles.bannerContent}>
                <span className={styles.badge}>💬 Join the Community</span>

                <h1>
                  Start chatting
                  <br />
                  with the world
                </h1>

                <p>Create your account and join thousands of users in real-time conversations.</p>

                <div className={styles.benefits}>
                  <span>💬 Instant messaging</span>
                  <span>🔒 Private & secure</span>
                  <span>👥 Group chats</span>
                </div>
              </div>
            </aside>

            <section className={styles.formSection}>
              <div className={styles.formWrapper}>
                <div className={styles.heading}>
                  <span className={styles.eyebrow}>Get started</span>
                  <h2>Create your account</h2>
                  <p>Join the conversation in seconds.</p>
                </div>

                <button 
                  className={styles.googleButton} 
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                >
                  <svg className={styles.googleIcon} viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
                  </svg>
                  Sign up with Google
                </button>

                <div className={styles.divider}>
                  <span>or sign up with email</span>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.field}>
                    <label htmlFor="name">Full name</label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="John Doe"
                      autoComplete="name"
                      disabled={loading}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      disabled={loading}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="password">Password</label>

                    <div className={styles.passwordWrapper}>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Minimum 6 characters"
                        autoComplete="new-password"
                        disabled={loading}
                      />

                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className={styles.submit} disabled={loading}>
                    {loading ? (
                      <>
                        <span className={styles.spinner} />
                        Creating account...
                      </>
                    ) : (
                      'Create account'
                    )}
                  </button>
                </form>

                <p className={styles.loginText}>
                  Already have an account? <Link to="/login">Sign in</Link>
                </p>
              </div>
            </section>
          </section>
        </main>
      </div>
    </>
  )
}

export default Register